<template>
  <div class="pos-report py-3">
    <header class="pos-report__toolbar">
      <div class="pos-report__title">
        <span class="pos-report__accent" aria-hidden="true"></span>
        <div>
          <h1>POS Sales</h1>
          <p>Daily, weekly and monthly performance of store sales</p>
        </div>
      </div>
      <div class="pos-report__toolbar-actions">
        <button
          type="button"
          class="pos-report__export"
          :class="{ 'is-loading': isExporting }"
          :disabled="!hasData || isLoading || isExporting"
          @click="exportReport"
        >
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          <span>{{ isExporting ? 'Preparing…' : 'Export CSV' }}</span>
        </button>
        <CButton
          size="sm"
          class="pos-report__refresh"
          :class="{ 'is-loading': isLoading }"
          :disabled="isLoading"
          @click="loadReport"
        >
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="23 4 23 10 17 10"/>
            <polyline points="1 20 1 14 7 14"/>
            <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/>
          </svg>
          <span>{{ isLoading ? 'Loading…' : 'Refresh' }}</span>
        </CButton>
      </div>
    </header>

    <CAlert v-if="errorMessage" color="danger" show class="pos-report__alert">{{ errorMessage }}</CAlert>

    <div class="pos-report__range">
      <DateRangePicker v-model="dateRange" @change="loadReport" />
    </div>

    <!-- Summary tiles -->
    <div class="pos-report__summary">
      <div class="summary-tile summary-tile--bills">
        <span class="summary-tile__label">Bills</span>
        <span class="summary-tile__value">{{ summary.bills }}</span>
        <span class="summary-tile__sub">{{ rangeLabel }}</span>
      </div>
      <div class="summary-tile summary-tile--revenue">
        <span class="summary-tile__label">Revenue</span>
        <span class="summary-tile__value">{{ currency(summary.revenue) }}</span>
        <span class="summary-tile__sub">Across {{ summary.bills }} bill{{ summary.bills === 1 ? '' : 's' }}</span>
      </div>
      <div class="summary-tile summary-tile--avg">
        <span class="summary-tile__label">Avg Bill</span>
        <span class="summary-tile__value">{{ currency(summary.avg_bill) }}</span>
        <span class="summary-tile__sub">Revenue ÷ bills</span>
      </div>
      <div class="summary-tile summary-tile--items">
        <span class="summary-tile__label">Items Sold</span>
        <span class="summary-tile__value">{{ summary.items }}</span>
        <span class="summary-tile__sub">Total quantity</span>
      </div>
    </div>

    <div v-if="isLoading" class="pos-report__loading">
      <CSpinner size="sm" /> <span>Loading report…</span>
    </div>

    <div v-else-if="!hasData" class="pos-report__empty">
      No POS bills in this range.
    </div>

    <template v-else>
      <!-- Per-org tiles -->
      <section class="pos-report__section">
        <header class="pos-report__head"><h2>By Organisation</h2></header>
        <div class="org-grid">
          <div v-for="row in byOrg" :key="`org-${row.organisation_id}`" :class="['org-tile', orgClass(row.organisation_name)]">
            <div class="org-tile__head">
              <img v-if="orgLogo(row.organisation_name)" :src="orgLogo(row.organisation_name)" :alt="row.organisation_name" />
              <span class="org-tile__name">{{ row.organisation_name }}</span>
            </div>
            <div class="org-tile__metrics">
              <div>
                <span class="org-tile__label">Bills</span>
                <span class="org-tile__value">{{ row.bills }}</span>
              </div>
              <div>
                <span class="org-tile__label">Revenue</span>
                <span class="org-tile__value">{{ currency(row.revenue) }}</span>
              </div>
            </div>
            <div class="org-tile__bar">
              <span class="org-tile__bar-fill" :style="{ width: orgShare(row.revenue) + '%' }"></span>
            </div>
          </div>
        </div>
      </section>

      <CRow>
        <CCol md="6">
          <section class="pos-report__section">
            <header class="pos-report__head">
              <h2>{{ byDayHeading }}</h2>
              <span class="pos-report__hint">{{ byDay.length }} period{{ byDay.length === 1 ? '' : 's' }}</span>
            </header>
            <div class="pos-report__table-wrap">
              <table class="pos-report__table">
                <thead>
                  <tr><th>Date</th><th class="num">Bills</th><th class="num">Revenue</th></tr>
                </thead>
                <tbody>
                  <tr v-for="row in byDay" :key="`day-${row.date}`">
                    <td>{{ formatDayLabel(row.date) }}</td>
                    <td class="num">{{ row.bills }}</td>
                    <td class="num"><strong>{{ currency(row.revenue) }}</strong></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        </CCol>
        <CCol md="6">
          <section class="pos-report__section">
            <header class="pos-report__head">
              <h2>Store Sale</h2>
              <span class="pos-report__hint">{{ byUser.length }} biller{{ byUser.length === 1 ? '' : 's' }}</span>
            </header>
            <div class="pos-report__table-wrap">
              <table class="pos-report__table">
                <thead>
                  <tr><th>User</th><th class="num">Bills</th><th class="num">Revenue</th></tr>
                </thead>
                <tbody>
                  <tr v-for="row in byUser" :key="`user-${row.user_id}`">
                    <td>{{ row.user_name }}</td>
                    <td class="num">{{ row.bills }}</td>
                    <td class="num"><strong>{{ currency(row.revenue) }}</strong></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        </CCol>
      </CRow>

      <CRow>
        <CCol md="6">
          <section class="pos-report__section">
            <header class="pos-report__head"><h2>By Payment Method</h2></header>
            <div class="pos-report__table-wrap">
              <table class="pos-report__table">
                <thead>
                  <tr><th>Method</th><th class="num">Bills</th><th class="num">Revenue</th></tr>
                </thead>
                <tbody>
                  <tr v-for="row in byPayment" :key="`pay-${row.payment_method}`">
                    <td>{{ formatLabel(row.payment_method) }}</td>
                    <td class="num">{{ row.bills }}</td>
                    <td class="num"><strong>{{ currency(row.revenue) }}</strong></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        </CCol>
        <CCol md="6">
          <section class="pos-report__section">
            <header class="pos-report__head">
              <h2>Telesales Credit (Care Of)</h2>
              <span class="pos-report__hint">{{ byCareOf.length }} agent{{ byCareOf.length === 1 ? '' : 's' }}</span>
            </header>
            <div v-if="!byCareOf.length" class="pos-report__sub-empty">
              No bills had a telesales credit in this range.
            </div>
            <div v-else class="pos-report__table-wrap">
              <table class="pos-report__table">
                <thead>
                  <tr><th>Agent</th><th class="num">Bills</th><th class="num">Revenue</th></tr>
                </thead>
                <tbody>
                  <tr v-for="row in byCareOf" :key="`care-${row.care_of}`">
                    <td>{{ row.care_of_name }}</td>
                    <td class="num">{{ row.bills }}</td>
                    <td class="num"><strong>{{ currency(row.revenue) }}</strong></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        </CCol>
      </CRow>
    </template>
  </div>
</template>

<script>
import axios from 'axios'
import DateRangePicker from '@/components/DateRangePicker.vue'

function toDateInput (d) {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function defaultRange () {
  // Last 7 days, inclusive of today.
  const today = new Date()
  const start = new Date(today)
  start.setDate(today.getDate() - 6)
  return { fromDate: toDateInput(start), toDate: toDateInput(today) }
}

export default {
  name: 'PosSalesReport',
  components: { DateRangePicker },
  data () {
    return {
      isLoading: false,
      isExporting: false,
      errorMessage: '',
      dateRange: defaultRange(),
      summary: { bills: 0, revenue: 0, avg_bill: 0, items: 0 },
      byDay: [],
      byOrg: [],
      byUser: [],
      byPayment: [],
      byCareOf: [],
    }
  },
  computed: {
    hasData () {
      return this.summary.bills > 0
    },
    rangeLabel () {
      const from = this.dateRange && this.dateRange.fromDate
      const to = this.dateRange && this.dateRange.toDate
      if (!from || !to) return ''
      if (from === to) return this.formatDayLabel(from)
      return `${this.formatDayLabel(from)} → ${this.formatDayLabel(to)}`
    },
    byDayHeading () {
      return 'Daily Breakdown'
    },
    totalOrgRevenue () {
      return this.byOrg.reduce((sum, row) => sum + Number(row.revenue || 0), 0)
    },
  },
  created () {
    this.loadReport()
  },
  methods: {
    async loadReport () {
      const from = this.dateRange && this.dateRange.fromDate
      const to = this.dateRange && this.dateRange.toDate
      if (!from || !to) {
        this.errorMessage = 'Pick a start and end date.'
        return
      }
      this.errorMessage = ''
      this.isLoading = true
      try {
        const response = await axios.get(`${this.$apiAdress}/api/reports/pos-sales`, {
          params: {
            start_date: from,
            end_date: to,
          },
          timeout: 20000,
        })
        const data = response?.data || {}
        this.summary = data.summary || { bills: 0, revenue: 0, avg_bill: 0, items: 0 }
        this.byDay = Array.isArray(data.by_day) ? data.by_day : []
        this.byOrg = Array.isArray(data.by_org) ? data.by_org : []
        this.byUser = Array.isArray(data.by_user) ? data.by_user : []
        this.byPayment = Array.isArray(data.by_payment_method) ? data.by_payment_method : []
        this.byCareOf = Array.isArray(data.by_care_of) ? data.by_care_of : []
      } catch (error) {
        console.log(error)
        this.errorMessage = error?.response?.data?.error || error?.message || 'Unable to load POS sales report.'
        this.summary = { bills: 0, revenue: 0, avg_bill: 0, items: 0 }
        this.byDay = []
        this.byOrg = []
        this.byUser = []
        this.byPayment = []
        this.byCareOf = []
      } finally {
        this.isLoading = false
      }
    },
    orgClass (name) {
      const lower = String(name || '').toLowerCase()
      // Veterinary Pharma is treated as part of the Agro family — same
      // green theme, same logo.
      if (lower.includes('agro') || lower.includes('veter')) return 'is-agro'
      if (lower.includes('drug')) return 'is-drugs'
      return 'is-default'
    },
    orgLogo (name) {
      const lower = String(name || '').toLowerCase()
      if (lower.includes('agro') || lower.includes('veter')) return '/MCCI-Agro.png'
      if (lower.includes('drug')) return '/MCCI-Drugs.png'
      return ''
    },
    orgShare (revenue) {
      const total = this.totalOrgRevenue
      if (!total) return 0
      return Math.max(2, Math.round((Number(revenue || 0) / total) * 100))
    },
    currency (value) {
      const n = Number(value || 0)
      if (!Number.isFinite(n)) return '₹0'
      return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`
    },
    formatLabel (value) {
      return String(value || '—').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    },
    formatDayLabel (value) {
      if (!value) return '—'
      const d = new Date(value)
      if (Number.isNaN(d.getTime())) return String(value)
      const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
      return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`
    },
    async exportReport () {
      if (!this.hasData) return
      const from = (this.dateRange && this.dateRange.fromDate) || ''
      const to = (this.dateRange && this.dateRange.toDate) || ''
      if (!from || !to) return

      this.errorMessage = ''
      this.isExporting = true
      try {
        const response = await axios.get(`${this.$apiAdress}/api/reports/pos-sales/download`, {
          params: { start_date: from, end_date: to },
          responseType: 'blob',
          timeout: 120000,
        })
        const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' })
        const url = window.URL.createObjectURL(blob)
        const anchor = document.createElement('a')
        anchor.href = url
        anchor.setAttribute('download', `POS Sales Report ${from} to ${to}.csv`)
        document.body.appendChild(anchor)
        anchor.click()
        anchor.remove()
        window.URL.revokeObjectURL(url)
      } catch (error) {
        console.log(error)
        let detail = ''
        const blob = error && error.response && error.response.data
        if (blob && typeof blob.text === 'function') {
          try {
            const text = await blob.text()
            try {
              const parsed = JSON.parse(text)
              detail = parsed.error || parsed.message || text
            } catch (e) {
              detail = text
            }
          } catch (e) {
            detail = ''
          }
        } else if (error && error.message) {
          detail = error.message
        }
        if (detail && detail.length > 300) detail = detail.slice(0, 300) + '…'
        const status = error && error.response && error.response.status
        const prefix = status ? `Unable to export (HTTP ${status})` : 'Unable to export POS sales report.'
        this.errorMessage = detail ? `${prefix}: ${detail}` : prefix
      } finally {
        this.isExporting = false
      }
    },
  },
}
</script>

<style scoped>
.pos-report {
  padding: 0.25rem 0;
}

.pos-report__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 0.25rem 0.25rem 1rem;
}

.pos-report__title {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.pos-report__accent {
  width: 4px;
  height: 36px;
  border-radius: 4px;
  background: linear-gradient(180deg, #0e5b46 0%, #16a07a 100%);
}

.pos-report__title h1 {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

.pos-report__title p {
  margin: 0;
  font-size: 0.78rem;
  color: #6b7280;
}

.pos-report__toolbar-actions {
  display: flex;
  gap: 0.5rem;
}

.pos-report__refresh {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: 8px;
  background: #fff;
  border: 1px solid #e5e9f2;
  color: #1f2a44;
  font-size: 0.82rem;
  font-weight: 600;
  padding: 0 0.85rem;
  height: 34px;
}

.pos-report__refresh:hover:not(:disabled) {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
}

.pos-report__export {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  height: 34px;
  padding: 0 0.95rem;
  border-radius: 8px;
  font-size: 0.82rem;
  font-weight: 600;
  color: #ffffff;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  border: 0;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
}

.pos-report__export:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28);
  filter: brightness(1.05);
}

.pos-report__export:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.28);
}

.pos-report__export:disabled {
  opacity: 0.55;
  cursor: not-allowed;
  box-shadow: none;
}

.pos-report__alert {
  margin: 0 0 0.75rem;
  border-radius: 10px;
}

/* Range picker wrapper — DateRangePicker handles its own internals;
   this just gives it breathing room from the surrounding cards. */
.pos-report__range {
  margin-bottom: 1rem;
}

/* Summary tiles */
.pos-report__summary {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.85rem;
  margin-bottom: 1rem;
}

.summary-tile {
  background: #fff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  padding: 0.85rem 1rem;
  border-left-width: 4px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.summary-tile--bills   { border-left-color: #1f2a44; }
.summary-tile--revenue { border-left-color: #0e5b46; }
.summary-tile--avg     { border-left-color: #2563eb; }
.summary-tile--items   { border-left-color: #f59e0b; }

.summary-tile__label {
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #6b7280;
}

.summary-tile__value {
  font-size: 1.55rem;
  font-weight: 800;
  color: #1f2a44;
  letter-spacing: -0.02em;
}

.summary-tile--revenue .summary-tile__value { color: #0e5b46; }

.summary-tile__sub {
  font-size: 0.74rem;
  color: #6b7280;
  margin-top: 2px;
}

/* States */
.pos-report__loading,
.pos-report__empty,
.pos-report__sub-empty {
  background: #fff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  padding: 2rem 1rem;
  text-align: center;
  color: #6b7280;
  font-size: 0.88rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
}

.pos-report__sub-empty {
  padding: 1.25rem 1rem;
  font-size: 0.82rem;
}

/* Sections */
.pos-report__section {
  background: #fff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  margin-bottom: 1rem;
  overflow: hidden;
}

.pos-report__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.7rem 1rem;
  border-bottom: 1px solid #eef1f7;
  background: #fbfcfe;
}

.pos-report__head h2 {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

.pos-report__hint {
  font-size: 0.74rem;
  color: #6b7280;
}

/* Org tiles */
.org-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 0.85rem;
  padding: 1rem;
}

.org-tile {
  background: #fbfdfc;
  border: 1px solid #eef1f7;
  border-radius: 10px;
  padding: 0.85rem;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.org-tile.is-agro { border-top: 3px solid #16a07a; }
.org-tile.is-drugs { border-top: 3px solid #2563eb; }

.org-tile__head {
  display: flex;
  align-items: center;
  gap: 0.55rem;
}

.org-tile__head img {
  width: 26px;
  height: 26px;
  object-fit: contain;
}

.org-tile__name {
  font-size: 0.92rem;
  font-weight: 700;
  color: #1f2a44;
}

.org-tile__metrics {
  display: flex;
  gap: 1.5rem;
}

.org-tile__metrics > div {
  display: flex;
  flex-direction: column;
}

.org-tile__label {
  font-size: 0.7rem;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.org-tile__value {
  font-size: 1.05rem;
  font-weight: 800;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

.org-tile.is-agro .org-tile__value:nth-child(2) { color: #0e5b46; }
.org-tile.is-drugs .org-tile__value:nth-child(2) { color: #1e3a8a; }

.org-tile__bar {
  height: 6px;
  border-radius: 999px;
  background: #eef1f7;
  overflow: hidden;
}

.org-tile__bar-fill {
  display: block;
  height: 100%;
  background: linear-gradient(90deg, #0e5b46 0%, #16a07a 100%);
  border-radius: 999px;
  transition: width 0.3s ease;
}

.org-tile.is-drugs .org-tile__bar-fill {
  background: linear-gradient(90deg, #1e3a8a 0%, #2563eb 100%);
}

/* Tables */
.pos-report__table-wrap {
  overflow-x: auto;
  max-height: 360px;
}

.pos-report__table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 0.84rem;
}

.pos-report__table thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #fbfcfe;
  border-bottom: 2px solid #e5e9f2;
  padding: 0.55rem 0.9rem;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #4b5563;
  text-align: left;
}

.pos-report__table th.num,
.pos-report__table td.num {
  text-align: right;
}

.pos-report__table tbody td {
  padding: 0.6rem 0.9rem;
  border-bottom: 1px solid #eef1f7;
  color: #374151;
}

.pos-report__table tbody tr:hover {
  background: #f8fafc;
}

.pos-report__table tbody tr:last-child td {
  border-bottom: 0;
}

@media (max-width: 991px) {
  .pos-report__summary {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 575px) {
  .pos-report__summary {
    grid-template-columns: 1fr;
  }
}
</style>
